import { EDocMetadataProps } from '@/components/EDocMetadata';
import { usePaymentDetail } from '@/features/approving_invoice_reports/components/PaymentDetailDrawer/context/PaymentDetailContext';
import {
  ButtonV2,
  IconMoreVert,
  Popover,
} from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useMemo } from 'react';
import { PopoverContent } from './components';
import styles from './Details.module.scss';
import { useDocumentMenuActions } from './hooks';

const cx = classNames.bind(styles);

export type Props = {
  eDocMeta?: EDocMetadataProps;
  onOpenFileInfoDrawer?: () => void;
  isEmpty?: boolean;
  isBusinessDocument?: boolean;
};

const DetailsInner: FC<Props> = memo(
  ({ eDocMeta, onOpenFileInfoDrawer, isEmpty, isBusinessDocument }) => {
    const {
      open,
      showMetadata,
      container,
      handleDownload,
      onClick,
      onClose,
      handleCloseMetadata,
      handleOpenMetadata,
    } = useDocumentMenuActions(isEmpty);

    const { activeFile } = usePaymentDetail();

    const handleOpenFileInfo = useCallback(() => {
      if (isBusinessDocument) {
        // Business document: open drawer
        onClose(); // Close the popover
        onOpenFileInfoDrawer?.(); // Open the drawer
      } else {
        // Invoice: show metadata in popover
        handleOpenMetadata();
      }
    }, [isBusinessDocument, onClose, onOpenFileInfoDrawer, handleOpenMetadata]);

    const renderContent = ({
      onClose: onCloseCallback,
    }: {
      onClose: VoidFunction;
    }) => (
      <PopoverContent
        onClose={onCloseCallback}
        showMetadata={showMetadata}
        onCloseMetadata={handleCloseMetadata}
        onOpenFileInfo={handleOpenFileInfo}
        onDownload={handleDownload}
        eDocProps={eDocMeta}
      />
    );

    const memorizedButton = useMemo(() => {
      return (
        <ButtonV2
          containerClassName={cx(styles['black-button'])}
          buttonContainerClassName={cx(styles['button-container'])}
          buttonClassName={cx(styles['button'])}
          testId='btn-open'
        >
          <IconMoreVert size={14} />
        </ButtonV2>
      );
    }, []);

    const handleOpenChange = useCallback(
      (isOpen: boolean) => {
        if (isOpen) {
          onClick();
        } else {
          onClose();
        }
      },
      [onClick, onClose]
    );

    const popoverProps = {
      open,
      onClose,
      onOpenChange: handleOpenChange,
      placement: 'bottomLeft' as const,
      openContent: memorizedButton,
      getPopupContainer: container,
      getTooltipContainer: container,
      content: renderContent,
      ...(showMetadata &&
        eDocMeta && {
          overlayClassName: cx(styles['popover-container']),
        }),
    };

    const popoverKey = activeFile
      ? `${activeFile.type}-${activeFile.id}`
      : 'no-file';

    return <Popover key={popoverKey} {...popoverProps} />;
  }
);
DetailsInner.displayName = 'DetailsInner';

// Wrapper component to prevent unnecessary hook calls when file is empty
// This optimization avoids executing useDocumentMenuActions when isEmpty is true
export const Details: FC<Props> = memo((props) => {
  if (props.isEmpty) {
    return null;
  }
  return <DetailsInner {...props} />;
});
Details.displayName = 'Details';
